export const TERMINOLOGY_CONTROLLED_SELECTOR = '[data-terminology-controlled="true"]';
const MAX_TOKEN_CODE_LENGTH = 160;
const MAX_USAGE_ID_LENGTH = 240;
const MAX_CONFIGURATION_CODE_LENGTH = 120;
const SAFE_IDENTITY_CODE = /^[a-z0-9][a-z0-9._-]*$/;

export interface TerminologyRenderIdentity {
  readonly tokenCode: string;
  readonly usageId: string;
  readonly configurationCode: string;
  readonly definitionKind: string | null;
  readonly usageKind: string | null;
  readonly localeCode: string | null;
  readonly mapRevision: string | null;
  readonly purposeCode: string | null;
  readonly sourceCode: string | null;
  readonly jurisdictionCode: string | null;
  readonly programCode: string | null;
  readonly channelCode: string | null;
  readonly audienceCode: string | null;
}

/**
 * Reads the stable identity emitted by a terminology rendering adapter.
 *
 * This deliberately requires the token, configuration, and usage attributes and never falls back
 * to visible or selected text. A focused composite widget may own the identity marker as a child
 * (for example, an AG Grid column header), so keyboard lookup supports those widgets explicitly.
 */
export function readTerminologyRenderIdentity(
  target: EventTarget | null,
): { element: HTMLElement; identity: TerminologyRenderIdentity } | null {
  const element = resolveTerminologyIdentityElement(target);
  if (!element) {
    return null;
  }

  const tokenCode = readRequiredAttribute(
    element,
    'data-terminology-token',
    MAX_TOKEN_CODE_LENGTH,
  );
  const usageId = readRequiredAttribute(
    element,
    'data-terminology-usage',
    MAX_USAGE_ID_LENGTH,
  );
  const configurationCode = readRequiredAttribute(
    element,
    'data-terminology-configuration',
    MAX_CONFIGURATION_CODE_LENGTH,
  );
  if (!tokenCode || !usageId || !configurationCode) {
    return null;
  }

  return {
    element,
    identity: {
      tokenCode,
      usageId,
      configurationCode,
      definitionKind: readOptionalAttribute(element, 'data-terminology-definition-kind'),
      usageKind: readOptionalAttribute(element, 'data-terminology-usage-kind'),
      localeCode: readOptionalAttribute(element, 'data-terminology-locale'),
      mapRevision: readOptionalAttribute(element, 'data-terminology-map-revision'),
      purposeCode: readOptionalAttribute(element, 'data-terminology-purpose'),
      sourceCode: readOptionalAttribute(element, 'data-terminology-source'),
      jurisdictionCode: readOptionalAttribute(element, 'data-terminology-jurisdiction'),
      programCode: readOptionalAttribute(element, 'data-terminology-program'),
      channelCode: readOptionalAttribute(element, 'data-terminology-channel'),
      audienceCode: readOptionalAttribute(element, 'data-terminology-audience'),
    },
  };
}

function resolveTerminologyIdentityElement(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) {
    return null;
  }

  const closestMarker = target.closest(TERMINOLOGY_CONTROLLED_SELECTOR);
  if (closestMarker instanceof HTMLElement) {
    return closestMarker;
  }

  const ownsFocusableContent = target.matches(
    'button, a, [role="button"], [role="columnheader"], [tabindex]',
  );
  if (!ownsFocusableContent) {
    return null;
  }

  const childMarker = target.querySelector(TERMINOLOGY_CONTROLLED_SELECTOR);
  return childMarker instanceof HTMLElement ? childMarker : null;
}

function readRequiredAttribute(
  element: HTMLElement,
  name: string,
  maximumLength: number,
): string | null {
  const value = readOptionalAttribute(element, name);
  return value
    && value.length <= maximumLength
    && SAFE_IDENTITY_CODE.test(value)
    ? value
    : null;
}

function readOptionalAttribute(element: HTMLElement, name: string): string | null {
  const value = element.getAttribute(name)?.trim();
  return value ? value : null;
}
